import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

// BS3-013: what staff recorded (Given / Refused / Withheld / Missed / PRN) on the admin participant Medications tab —
// it was not shown anywhere on the admin side.
export const MAR_OUTCOME_LABELS = { ADMINISTERED: 'Given', PRN_GIVEN: 'PRN given', REFUSED: 'Refused', WITHHELD: 'Withheld', MISSED: 'Missed' };
const MAR_OUTCOME_TONE = { ADMINISTERED: 'text-emerald-700', PRN_GIVEN: 'text-emerald-700', REFUSED: 'text-amber-700', WITHHELD: 'text-amber-700', MISSED: 'text-red-700' };

export function MedicationAdministrationRecord({ participantId }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    setError('');
    try {
      const r = await api.get(`/emar/by-participant/${participantId}`, { params: { pageSize: 50 } });
      setRows(r.data.items || r.data.data || []);
    } catch (e) {
      setRows([]);
      setError(e?.response?.status === 403 ? 'You do not have permission to view the administration record.' : 'Unable to load the administration record.');
    }
  }, [participantId]);
  useEffect(() => { load(); }, [load]);
  const when = (r) => {
    const t = r.administered_at || r.created_at;
    try { return t ? new Date(t).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }) : '—'; } catch { return t || '—'; }
  };
  return (
    <div className="mt-6 space-y-2" data-testid="med-admin-record">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-sm">Administration record</h4>
        <Button size="sm" variant="ghost" onClick={load} data-testid="med-admin-record-refresh">Refresh</Button>
      </div>
      {rows === null ? <p className="text-sm text-muted-foreground">Loading…</p>
        : error ? <p className="text-sm text-muted-foreground" data-testid="med-admin-record-error">{error}</p>
          : rows.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="med-admin-record-empty">No doses recorded yet.</p>
            : (
              <Table>
                <TableHeader><TableRow><TableHead>Recorded</TableHead><TableHead>Scheduled</TableHead><TableHead>Medication</TableHead><TableHead>Dose</TableHead><TableHead>Outcome</TableHead><TableHead>Staff</TableHead><TableHead>Notes</TableHead></TableRow></TableHeader>
                <TableBody>{rows.map((r) => (
                  <TableRow key={r.id} data-testid={`med-admin-row-${r.id}`}>
                    <TableCell className="text-xs whitespace-nowrap">{when(r)}</TableCell>
                    <TableCell className="text-xs font-mono">{r.scheduled_time || '—'}</TableCell>
                    <TableCell className="text-sm font-medium">{r.medication_name || '—'}</TableCell>
                    <TableCell className="text-sm">{r.dose_given || r.dosage || '—'}</TableCell>
                    <TableCell className={`text-sm font-medium ${MAR_OUTCOME_TONE[r.status] || ''}`} data-testid={`med-admin-outcome-${r.id}`}>
                      {MAR_OUTCOME_LABELS[r.status] || r.status}
                      {r.requires_double_sign && <span className="block text-[10px] font-normal text-muted-foreground">{r.double_signed_at || r.second_signed_at ? `Double-signed${r.double_signed_by_name ? ` by ${r.double_signed_by_name}` : ''}` : 'Awaiting second sign'}</span>}
                    </TableCell>
                    <TableCell className="text-sm">{r.staff_name || '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[220px] truncate" title={[r.reason, r.notes].filter(Boolean).join(' — ')}>{[r.reason, r.notes].filter(Boolean).join(' — ') || '—'}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            )}
    </div>
  );
}

export default MedicationAdministrationRecord;
